import React, { useRef, useEffect, useState } from "react";
import {
	Dialog,
	Typography,
	makeStyles,
	Tabs,
	Tab,
	Paper,
	IconButton,
	Tooltip,
} from "@material-ui/core";
import { Code, Visibility, PhotoLibrary } from "@material-ui/icons";
import AceEditor from "react-ace";
import { setupMessageTemplatesMappableFieldsHighlighting } from "../utils/aceEditorUtils";
import AppBarComponent from "../../../../Common/Headers/AppBarComponent";
import MediaLibraryModal from "../../../../Common/MediaLibrary/MediaLibraryModal";

import "ace-builds/src-noconflict/mode-html";
import "ace-builds/src-noconflict/theme-textmate";
import "ace-builds/src-noconflict/ext-language_tools";

const useStyles = makeStyles((theme) => ({
	appBar: {
		position: "relative",
		backgroundColor: "#53B3EA",
	},
	content: {
		padding: theme.spacing(2),
		height: "100vh",
		display: "flex",
		flexDirection: "column",
	},
	editor: {
		flex: 1,
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: 4,
	},
	infoText: {
		marginTop: theme.spacing(1),
		color: theme.palette.text.secondary,
	},
	tabsContainer: {
		backgroundColor: theme.palette.background.default,
		marginBottom: theme.spacing(2),
		display: "flex",
		alignItems: "center",
	},
	tabsWrapper: {
		flex: 1,
	},
	previewContainer: {
		flex: 1,
		padding: theme.spacing(2),
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: 4,
		backgroundColor: "#fff",
	},
	htmlPreview: {
		width: "100%",
		height: "100%",
	},
}));

const FullScreenEditor = ({
	open,
	onClose,
	value,
	onChange,
	onSave,
	resources,
	dealerGroupId,
	markPreviewAsViewed,
	showPreview,
	useCase,
	messageType,
}) => {
	const classes = useStyles();
	const editorRef = useRef(null);
	const highlighterRef = useRef(null);
	const [viewMode, setViewMode] = useState(0); // 0 = edit, 1 = preview
	const [mediaLibraryOpen, setMediaLibraryOpen] = useState(false);

	const useCaseId = useCase?.useCase;

	const handleEditorLoad = (editor) => {
		if (!open) {
			return;
		}

		const textarea = editor.textInput.getElement();
		if (textarea) {
			textarea.setAttribute("name", "full-screen-html-editor");
			textarea.setAttribute("id", "full-screen-html-editor");
		}

		if (highlighterRef.current) {
			highlighterRef.current.dispose();
			highlighterRef.current = null;
		}

		highlighterRef.current = setupMessageTemplatesMappableFieldsHighlighting(
			editor,
			useCaseId,
			messageType
		);
	};

	useEffect(() => {
		if (highlighterRef.current && open) {
			highlighterRef.current.updateContext(useCaseId, messageType);
		}
	}, [useCaseId, messageType, open]);

	// Cleanup when dialog closes
	useEffect(() => {
		if (!open && highlighterRef.current) {
			highlighterRef.current.dispose();
			highlighterRef.current = null;
		}
	}, [open]);

	useEffect(() => {
		if (showPreview && viewMode !== 1) {
			setViewMode(1);
			markPreviewAsViewed();
		}
	}, [showPreview, viewMode, markPreviewAsViewed]);

	const handleTabChange = (event, newValue) => {
		setViewMode(newValue);

		if (newValue === 1) {
			markPreviewAsViewed();
		}
	};

	const handleEditorChange = (newValue) => {
		onChange(newValue);
	};

	const handleSave = () => {
		onSave();
		onClose();
	};

	const handleOpenMediaLibrary = () => {
		setMediaLibraryOpen(true);
	};

	const handleCloseMediaLibrary = () => {
		setMediaLibraryOpen(false);
	};

	return (
		<Dialog fullScreen open={open} onClose={onClose}>
			<AppBarComponent
				idName="fullScreenEditor"
				title={resources["FullScreenHTMLEditor"]}
				resources={resources}
				handleClose={onClose}
				handleSubmit={handleSave}
				rightButtonResource={resources["CloseFullScreenEditor"]}
			/>
			<div className={classes.content}>
				<Paper className={classes.tabsContainer}>
					<div className={classes.tabsWrapper}>
						<Tabs
							value={viewMode}
							onChange={handleTabChange}
							indicatorColor="primary"
							textColor="primary"
							variant="fullWidth"
						>
							<Tab icon={<Code />} label={resources["HTMLEditor"]} />
							<Tab icon={<Visibility />} label={resources["Preview"]} />
						</Tabs>
					</div>
					<Tooltip title={resources["MediaLibraryTooltip"]}>
						<IconButton
							onClick={handleOpenMediaLibrary}
							style={{ marginRight: 8 }}
						>
							<PhotoLibrary />
						</IconButton>
					</Tooltip>
				</Paper>

				{viewMode === 0 ? (
					<div className={classes.editor}>
						<AceEditor
							ref={editorRef}
							mode="html"
							theme="textmate"
							onChange={handleEditorChange}
							onLoad={handleEditorLoad}
							value={value || ""}
							name="full-screen-html-editor-container"
							editorProps={{ $blockScrolling: true }}
							setOptions={{
								enableBasicAutocompletion: true,
								enableLiveAutocompletion: true,
								enableSnippets: true,
								showLineNumbers: true,
								tabSize: 2,
								useWorker: false,
							}}
							width="100%"
							height="100%"
							fontSize={16}
							showPrintMargin={false}
						/>
					</div>
				) : (
					<Paper className={classes.previewContainer}>
						<div
							className={classes.htmlPreview}
							dangerouslySetInnerHTML={{
								__html: value || `<p>${resources["NoContentToPreview"]}</p>`,
							}}
						/>
					</Paper>
				)}

				{viewMode === 0 && (
					<Typography variant="caption" className={classes.infoText}>
						{resources["MappableFieldsInfoCaption"]}
					</Typography>
				)}
			</div>

			{mediaLibraryOpen && (
				<MediaLibraryModal
					open={mediaLibraryOpen}
					onClose={handleCloseMediaLibrary}
					module="messageTemplates"
					resources={resources}
					dealerGroupId={dealerGroupId}
				/>
			)}
		</Dialog>
	);
};

export default FullScreenEditor;
